import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { PRODUCTS, PRODUCT_LINKS } from '../../constants';

import ExchangeIcon from '../../../images/icons/icon-sport-svg/exchange.svg';
import ZeroIcon from '../../../images/icons/icon-sport-svg/zero.svg';
import CasinoIcon from '../../../images/icons/icon-sport-svg/casino.svg';
import LiveCasinoIcon from '../../../images/icons/icon-sport-svg/live-casino.svg';
import VirtualsIcon from '../../../images/icons/icon-sport-svg/virtuals.svg';

import IconMenu from '../../../images/icons/misc/burger.svg';
import IconMyBets from '../../../images/icons/misc/betslip.svg';
import IconMultiples from '../../../images/icons/misc/icon-multiples.svg';
import IconPromos from '../../../images/icons/misc/promos-icon.svg';

import style from './MobileFooter.module.scss';

const MobileFooter = ({
  history: { push: navigate },
  location: { pathname },
  toggleNavigation,
  navigationVisible,
  updateRightSidebar,
  sidebarVisible,
  sidebarContent,
  product,
}) => {
  const [selectedItem, setSelectedItem] = useState('home');

  const prevRef = useRef();

  const getCompleteClass = (key, gtm) => (
    classNames({
      [style.footerItem]: true,
      [style.selected]: selectedItem === key,
      '.gtm_cta': gtm,
    })
  );

  const handleClick = (clickedItem) => () => {
    if (clickedItem === 'home') {
      prevRef.current = clickedItem;
      updateRightSidebar({
        visible: false
      });

      if (clickedItem === 'home') {
        const { url: productHomeUrl } = PRODUCT_LINKS.find(link => link.id === product) || {};

        navigate(productHomeUrl);
      }
    }

    if (clickedItem === 'promotions') {
      toggleNavigation(!navigationVisible);
      const promotionsPageUrl =
        [PRODUCTS.exchange, PRODUCTS.mbzero].includes(product) ? '/promotions/exchange' : '/promotions/casino';
      navigate(promotionsPageUrl);
    }

    if (clickedItem === 'menu') {
      toggleNavigation(!navigationVisible);
    } else {
      toggleNavigation(false);
    }

    if (['multiples', 'betslip'].includes(clickedItem)) {
      updateRightSidebar({
        content: clickedItem,
        visible: sidebarContent !== clickedItem || !sidebarVisible
      });
    }

    setSelectedItem(selectedItem === clickedItem ? 'home' : clickedItem);
  };

  const homeButton = useMemo(() => (
    <div
      role="button"
      className={getCompleteClass('home', true)}
      onClick={handleClick('home')}
      data-hook={`mob-footer-${product}`}
    >
      {product === PRODUCTS.exchange && (
        <Fragment>
          <ExchangeIcon className={style.icon} />
          <span className={style.label}>Exchange</span>
        </Fragment>
      )}
      {product === PRODUCTS.mbzero && (
        <Fragment>
          <ZeroIcon className={style.icon} />
          <span className={style.label}>MB Zero</span>
        </Fragment>
      )}
      {product === PRODUCTS.casino && (
        <Fragment>
          <CasinoIcon className={style.icon} />
          <span className={style.label}>Casino</span>
        </Fragment>
      )}
      {product === PRODUCTS.liveCasino && (
        <Fragment>
          <LiveCasinoIcon className={style.icon} />
          <span className={style.label}>Live Casino</span>
        </Fragment>
      )}
      {product === PRODUCTS.virtuals && (
        <Fragment>
          <VirtualsIcon className={style.icon} />
          <span className={style.label}>Virtuals</span>
        </Fragment>
      )}
    </div>
  ), [product, selectedItem]);

  useEffect(() => {
    if (!navigationVisible && !sidebarVisible) {
      if (pathname === '/promotions') {
        setSelectedItem('promotions');
      } else {
        setSelectedItem('home');
      }
    } else {
      setSelectedItem('menu');
    }
  }, [navigationVisible, pathname, sidebarVisible]);

  useEffect(() => {
    if (sidebarVisible) {
      setSelectedItem(sidebarContent);
    }

    if ((!sidebarVisible && !navigationVisible)) {
      if (pathname === '/promotions') {
        setSelectedItem('promotions');
      } else {
        setSelectedItem('home');
      }
    }
  }, [sidebarContent, sidebarVisible, pathname, navigationVisible]);

  return (
    <div className={style.mobileFooter}>
      {homeButton}
      <div
        role="button"
        className={getCompleteClass('menu', false)}
        onClick={handleClick('menu')}
      >
        <IconMenu className={style.icon} />
        <span className={style.label}>Menu</span>
      </div>
      <div
        role="button"
        className={getCompleteClass('betslip', true)}
        onClick={handleClick('betslip')}
        data-hook="mob-footer-mybets"
      >
        <IconMyBets className={style.icon} />
        <span className={style.label}>My Bets</span>
      </div>
      <div
        role="button"
        className={getCompleteClass('multiples', true)}
        onClick={handleClick('multiples')}
        data-hook="mob-footer-multiples"
      >
        <IconMultiples className={style.icon} />
        <span className={style.label}>Multiples</span>
      </div>
      <div
        role="button"
        className={getCompleteClass('promotions', true)}
        onClick={handleClick('promotions')}
        data-hook="mob-footer-promotions"
      >
        <IconPromos className={style.icon} />
        <span className={style.label}>Promotions</span>
      </div>
    </div>
  );
};

MobileFooter.propTypes = {
  sidebarContent: PropTypes.string,
  sidebarVisible: PropTypes.bool,
  navigationVisible: PropTypes.bool,
  updateRightSidebar: PropTypes.func.isRequired,
  toggleNavigation: PropTypes.func.isRequired,
  product: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  })
};

export default MobileFooter;
